import type { LuminarySettings, TemplateBundle } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';

function createDataLuminary(settings = {}) {
    return new Luminary({
        fetchResources: false,
        disableRenderer: true,
        ...settings,
    });
}

type DataLuminarySettings = {
    templateBundle?: TemplateBundle;
    luminarySettings?: LuminarySettings;
};

/**
 * Performs some work with a data luminary, automatically destroying the luminary instance after the work is done.
 *
 * @param work the method which receives the data luminary instance and executes the work
 * @param templateBundle The bundle, e.g. getTemplateBundle(getState(), templateId).
 * @return The result of the work.
 */
export async function withDataLuminary<T>(
    work: (dataLuminary: Luminary) => Promise<T>,
    { templateBundle, luminarySettings }: DataLuminarySettings = {},
): Promise<T> {
    const dataLuminary = createDataLuminary(luminarySettings);

    try {
        if (templateBundle) {
            dataLuminary.setTemplate(templateBundle);
        }
        return await work(dataLuminary);
    } finally {
        dataLuminary.destroy();
    }
}

/**
 * Performs some synchronous work with a data luminary instance, automatically destroying the
 * luminary instance after the work is done.
 *
 * @param templateBundle The bundle, e.g. getTemplateBundle(getState(), templateId).
 * @param work the method which receives the data luminary instance and executes the work
 * @return The result of the work.
 */
export function withDataLuminarySync<T>(
    work: (dataLuminary: Luminary) => T,
    { templateBundle, luminarySettings }: DataLuminarySettings = {},
): T {
    const dataLuminary = createDataLuminary(luminarySettings);

    try {
        if (templateBundle) {
            dataLuminary.setTemplate(templateBundle);
        }
        return work(dataLuminary);
    } finally {
        dataLuminary.destroy();
    }
}
